/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './hospital-bill-item.reducer';

export const HospitalBillItem = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const hospitalBillItemList = useAppSelector(state => state.hospitalBillItem.entities);
  const loading = useAppSelector(state => state.hospitalBillItem.loading);
  const totalItems = useAppSelector(state => state.hospitalBillItem.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="hospital-bill-item-heading" data-cy="HospitalBillItemHeading">
        <Translate contentKey="tinyMedicRestApp.hospitalBillItem.home.title">Hospital Bill Items</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.hospitalBillItem.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/hospital-bill-item/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.hospitalBillItem.home.createLabel">Create new Hospital Bill Item</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.hospitalBillItem.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {hospitalBillItemList && hospitalBillItemList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('descreption')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.descreption">Descreption</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('descreption')} />
                </th>
                <th className="hand" onClick={sort('remainingQty')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.remainingQty">Remaining Qty</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('remainingQty')} />
                </th>
                <th className="hand" onClick={sort('rate')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.rate">Rate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('rate')} />
                </th>
                <th className="hand" onClick={sort('discountRate')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.discountRate">Discount Rate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('discountRate')} />
                </th>
                <th className="hand" onClick={sort('marginRate')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.marginRate">Margin Rate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('marginRate')} />
                </th>
                <th className="hand" onClick={sort('netRate')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.netRate">Net Rate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('netRate')} />
                </th>
                <th className="hand" onClick={sort('grossValue')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.grossValue">Gross Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('grossValue')} />
                </th>
                <th className="hand" onClick={sort('discount')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.discount">Discount</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('discount')} />
                </th>
                <th className="hand" onClick={sort('tax')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.tax">Tax</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tax')} />
                </th>
                <th className="hand" onClick={sort('netValue')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.netValue">Net Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('netValue')} />
                </th>
                <th className="hand" onClick={sort('taxPlusNetValue')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.taxPlusNetValue">Tax Plus Net Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('taxPlusNetValue')} />
                </th>
                <th className="hand" onClick={sort('marginValue')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.marginValue">Margin Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('marginValue')} />
                </th>
                <th className="hand" onClick={sort('adjustedValue')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.adjustedValue">Adjusted Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('adjustedValue')} />
                </th>
                <th className="hand" onClick={sort('billType')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.billType">Bill Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billType')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampEntry')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.timestampEntry">Timestamp Entry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampEntry')} />
                </th>
                <th className="hand" onClick={sort('lastUpdated')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.lastUpdated">Last Updated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastUpdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('miscellaneous')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.miscellaneous">Miscellaneous</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('miscellaneous')} />
                </th>
                <th className="hand" onClick={sort('multiplier')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.multiplier">Multiplier</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('multiplier')} />
                </th>
                <th className="hand" onClick={sort('paymentMethod')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.paymentMethod">Payment Method</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('paymentMethod')} />
                </th>
                <th className="hand" onClick={sort('paymentStatus')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.paymentStatus">Payment Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('paymentStatus')} />
                </th>
                <th className="hand" onClick={sort('roundOffVal')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.roundOffVal">Round Off Val</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('roundOffVal')} />
                </th>
                <th className="hand" onClick={sort('chargedItem')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.chargedItem">Charged Item</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('chargedItem')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.bill">Bill</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.patientCategory">Patient Category</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.hospitalPayment">Hospital Payment</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalBillItem.scheme">Scheme</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hospitalBillItemList.map((hospitalBillItem, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/hospital-bill-item/${hospitalBillItem.id}`} color="link" size="sm">
                      {hospitalBillItem.id}
                    </Button>
                  </td>
                  <td>{hospitalBillItem.descreption}</td>
                  <td>{hospitalBillItem.remainingQty}</td>
                  <td>{hospitalBillItem.rate}</td>
                  <td>{hospitalBillItem.discountRate}</td>
                  <td>{hospitalBillItem.marginRate}</td>
                  <td>{hospitalBillItem.netRate}</td>
                  <td>{hospitalBillItem.grossValue}</td>
                  <td>{hospitalBillItem.discount}</td>
                  <td>{hospitalBillItem.tax}</td>
                  <td>{hospitalBillItem.netValue}</td>
                  <td>{hospitalBillItem.taxPlusNetValue}</td>
                  <td>{hospitalBillItem.marginValue}</td>
                  <td>{hospitalBillItem.adjustedValue}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.BillType.${hospitalBillItem.billType}`} />
                  </td>
                  <td>{hospitalBillItem.active ? 'true' : 'false'}</td>
                  <td>
                    {hospitalBillItem.microSec ? (
                      <TextFormat type="date" value={hospitalBillItem.microSec} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {hospitalBillItem.timestampEntry ? (
                      <TextFormat type="date" value={hospitalBillItem.timestampEntry} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {hospitalBillItem.lastUpdated ? (
                      <TextFormat type="date" value={hospitalBillItem.lastUpdated} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{hospitalBillItem.cursor}</td>
                  <td>{hospitalBillItem.miscellaneous ? 'true' : 'false'}</td>
                  <td>{hospitalBillItem.multiplier}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PaymentMethod.${hospitalBillItem.paymentMethod}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PaymentStatus.${hospitalBillItem.paymentStatus}`} />
                  </td>
                  <td>{hospitalBillItem.roundOffVal}</td>
                  <td>{hospitalBillItem.chargedItem}</td>
                  <td>
                    {hospitalBillItem.bill ? <Link to={`/hospital-bill/${hospitalBillItem.bill.id}`}>{hospitalBillItem.bill.id}</Link> : ''}
                  </td>
                  <td>
                    {hospitalBillItem.patientCategory ? (
                      <Link to={`/patient-category/${hospitalBillItem.patientCategory.id}`}>{hospitalBillItem.patientCategory.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {hospitalBillItem.hospitalPayment ? (
                      <Link to={`/hospital-payment/${hospitalBillItem.hospitalPayment.id}`}>{hospitalBillItem.hospitalPayment.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {hospitalBillItem.scheme ? (
                      <Link to={`/payment-scheme/${hospitalBillItem.scheme.id}`}>{hospitalBillItem.scheme.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/hospital-bill-item/${hospitalBillItem.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/hospital-bill-item/${hospitalBillItem.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/hospital-bill-item/${hospitalBillItem.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.hospitalBillItem.home.notFound">No Hospital Bill Items found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={hospitalBillItemList && hospitalBillItemList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default HospitalBillItem;
